import '../Sass/App.scss';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../Helpers/api';

const CLI_NAME = 'command line interface';

function normalize(text) {
    return String(text || '').toLowerCase().trim();
}

function compact(text) {
    return normalize(text).replace(/\s+/g, ' ');
}

function commandResult(display, command, description = '') {
    return {
        display,
        command,
        description,
        rank: 0
    };
}

function productTerms(item) {
    return [
        item.name,
        item.nickname,
        item.designer,
        item.description
    ].filter(Boolean).map(compact);
}

function rankMatch(query, terms) {
    const normalizedQuery = compact(query);
    if (!normalizedQuery) {
        return null;
    }

    let bestRank = null;
    terms.forEach((term) => {
        if (term === normalizedQuery) {
            bestRank = bestRank === null ? 1 : Math.min(bestRank, 1);
        } else if (term.startsWith(normalizedQuery)) {
            bestRank = bestRank === null ? 2 : Math.min(bestRank, 2);
        } else if (term.includes(normalizedQuery)) {
            bestRank = bestRank === null ? 3 : Math.min(bestRank, 3);
        }
    });

    return bestRank;
}

export function Cli(props) {
    const [query, setQuery] = useState('');
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [codebases, setCodebases] = useState([]);
    const [bootkits, setBootkits] = useState([]);
    const [results, setResults] = useState([]);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(null);
    const [transcript, setTranscript] = useState([
        { type: 'output', text: 'system ready. type help for available commands.' }
    ]);
    const inputRef = useRef(null);
    const compactInputRef = useRef(null);

    function isTypingTarget(target) {
        if (!target) {
            return false;
        }

        const tagName = target.tagName ? target.tagName.toLowerCase() : '';
        return tagName === 'input'
            || tagName === 'textarea'
            || tagName === 'select'
            || target.isContentEditable;
    }

    useEffect(() => {
        Promise.all([
            apiFetch('codebases.php').then((res) => res.json()).catch(() => ({ data: [] })),
            apiFetch('bootkits.php').then((res) => res.json()).catch(() => ({ data: [] }))
        ]).then(([codebaseResponse, bootkitResponse]) => {
            setCodebases(codebaseResponse.data || []);
            setBootkits(bootkitResponse.data || []);
        });
    }, []);

    const baseCommands = useMemo(() => ([
        commandResult('help', 'help', 'show available commands'),
        commandResult('shop codebases', 'shop codebases', 'browse codebases'),
        commandResult('shop bootkits', 'shop bootkits', 'browse bootkits'),
        commandResult('documentation', 'documentation', 'open codebase documentation'),
        commandResult('rules', 'rules', 'open game rules'),
        commandResult('cart', 'cart', 'open shopping cart'),
        commandResult('cart', 'checkout', 'open shopping cart'),
        commandResult('login', 'login', 'open profile login'),
        commandResult('register', 'register', 'create a profile'),
        commandResult('messages', 'messages', 'open profile messages'),
        commandResult('commissions', 'commissions', 'open commission ledger'),
        commandResult('agreement', 'agreement', 'open commission agreement'),
        commandResult('playtest', 'playtest', 'open playtest setup'),
        commandResult('clear', 'clear', 'clear terminal output'),
        commandResult('whoami', 'whoami', 'identify current session'),
        commandResult('home', 'home', 'return to the home page'),
        commandResult('ls', 'ls', 'list primary directories')
    ]), []);

    const buildResults = useCallback((nextQuery) => {
        const normalizedQuery = compact(nextQuery);
        if (normalizedQuery.length < 1) {
            return [];
        }

        const commandMatches = baseCommands
            .map((item) => ({
                ...item,
                rank: rankMatch(normalizedQuery, [compact(item.command), compact(item.display), compact(item.description)])
            }))
            .filter((item) => item.rank !== null);

        const codebaseMatches = codebases
            .map((codebase) => ({
                display: codebase.name,
                command: `docs ${codebase.name}`,
                description: codebase.designer ? `codebase by ${codebase.designer}` : 'codebase documentation',
                type: 'codebase',
                codebase,
                rank: rankMatch(normalizedQuery, productTerms(codebase))
            }))
            .filter((item) => item.rank !== null);

        const bootkitMatches = bootkits
            .map((bootkit) => ({
                display: bootkit.name,
                command: 'shop bootkits',
                description: 'bootkit product',
                type: 'bootkit',
                bootkit,
                rank: rankMatch(normalizedQuery, [compact(bootkit.name), compact(bootkit.description), compact(bootkit.components)])
            }))
            .filter((item) => item.rank !== null);

        return [...commandMatches, ...codebaseMatches, ...bootkitMatches]
            .sort((a, b) => a.rank - b.rank || a.display.localeCompare(b.display))
            .slice(0, 7);
    }, [baseCommands, bootkits, codebases]);

    const setQueryAndResults = useCallback((nextQuery) => {
        setQuery(nextQuery);
        setResults(buildResults(nextQuery));
    }, [buildResults]);

    useEffect(() => {
        function handleGlobalKeyDown(event) {
            if (overlayOpen || event.ctrlKey || event.metaKey || event.altKey || isTypingTarget(event.target)) {
                return;
            }

            if (event.key.length !== 1) {
                return;
            }

            event.preventDefault();
            setQueryAndResults(event.key);
            setOverlayOpen(true);
            window.setTimeout(() => inputRef.current?.focus(), 0);
        }

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [overlayOpen, setQueryAndResults]);

    function addTranscript(prompt, output) {
        setTranscript((currentTranscript) => [
            ...currentTranscript,
            { type: 'prompt', text: `> ${prompt}` },
            { type: 'output', text: output }
        ].slice(-10));
    }

    function findCodebase(searchText) {
        const normalizedSearch = compact(searchText);
        if (!normalizedSearch) {
            return null;
        }

        return codebases.find((codebase) => productTerms(codebase).some((term) => term === normalizedSearch))
            || codebases.find((codebase) => productTerms(codebase).some((term) => term.startsWith(normalizedSearch)))
            || codebases.find((codebase) => productTerms(codebase).some((term) => term.includes(normalizedSearch)))
            || null;
    }

    function executeCommand(rawCommand) {
        const command = compact(rawCommand);
        if (!command) {
            return;
        }

        setHistory((currentHistory) => [...currentHistory, rawCommand].slice(-25));
        setHistoryIndex(null);

        if (command === 'clear') {
            setTranscript([]);
            setQueryAndResults('');
            return;
        }

        if (command === 'help') {
            addTranscript(rawCommand, 'commands: shop bootkits, shop codebases, docs <codebase>, playtest <codebase>, cart, profile, messages, commissions, agreement, rules, clear');
        } else if (command === 'ls') {
            addTranscript(rawCommand, 'shop/ documentation/ playtest/ cart/ profile/');
        } else if (command === 'whoami') {
            addTranscript(rawCommand, 'guest session');
        } else if (command === 'cart' || command === 'checkout' || command === 'sudo checkout') {
            props.openCart();
            addTranscript(rawCommand, 'opening cart...');
            setOverlayOpen(false);
        } else if (command === 'login' || command === 'profile') {
            props.openProfile('login');
            addTranscript(rawCommand, 'opening profile...');
            setOverlayOpen(false);
        } else if (command === 'register' || command === 'signup') {
            props.openProfile('register');
            addTranscript(rawCommand, 'opening account creation...');
            setOverlayOpen(false);
        } else if (command === 'messages' || command === 'inbox') {
            props.openProfile('messages');
            addTranscript(rawCommand, 'opening messages...');
            setOverlayOpen(false);
        } else if (command === 'earnings' || command === 'commissions') {
            props.openProfile('commissions');
            addTranscript(rawCommand, 'opening commission ledger...');
            setOverlayOpen(false);
        } else if (command === 'agreement' || command === 'commission agreement' || command === 'terms') {
            props.openProfile('agreement');
            addTranscript(rawCommand, 'opening commission agreement...');
            setOverlayOpen(false);
        } else if (command === 'shop' || command === 'buy' || command === 'shop codebases' || command === 'spectrum') {
            props.changePage('shop', 'shop codebases');
            addTranscript(rawCommand, 'opening shop/codebases...');
            setOverlayOpen(false);
        } else if (command === 'shop bootkits' || command === 'bootkits' || command === 'boot kits') {
            props.changePage('shop', 'shop bootkits');
            addTranscript(rawCommand, 'opening shop/bootkits...');
            setOverlayOpen(false);
        } else if (command === 'documentation' || command === 'docs' || command === 'latest') {
            props.changePage('documentation', 0);
            addTranscript(rawCommand, 'opening documentation...');
            setOverlayOpen(false);
        } else if (command === 'home') {
            props.changePage('malware', 0);
            addTranscript(rawCommand, 'returning home...');
            setOverlayOpen(false);
        } else if (command.startsWith('docs ') || command.startsWith('documentation ')) {
            const searchText = command.replace(/^docs\s+|^documentation\s+/, '');
            const codebase = findCodebase(searchText);
            if (codebase) {
                props.changePage('documentation', codebase.codebase_id);
                addTranscript(rawCommand, `opening docs/${codebase.name}...`);
                setOverlayOpen(false);
            } else {
                addTranscript(rawCommand, `no codebase found for "${searchText}"`);
            }
        } else if (command === 'playtest' || command === 'play') {
            props.changePage('playtest engine', 0);
            addTranscript(rawCommand, 'opening playtest setup...');
            setOverlayOpen(false);
        } else if (command.startsWith('playtest ') || command.startsWith('play ')) {
            const searchText = command.replace(/^playtest\s+|^play\s+/, '');
            const codebase = findCodebase(searchText);
            if (codebase) {
                props.changePage('playtest engine', codebase.codebase_id);
                addTranscript(rawCommand, `opening playtest/${codebase.name}...`);
                setOverlayOpen(false);
            } else {
                addTranscript(rawCommand, `no codebase found for "${searchText}"`);
            }
        } else if (command === 'rules' || command === 'learn' || command === 'instructions') {
            props.changePage('rules', 0);
            addTranscript(rawCommand, 'opening rules...');
            setOverlayOpen(false);
        } else if (command === 'dev guide' || command === 'developer guide') {
            props.changePage('devguide', 0);
            addTranscript(rawCommand, 'opening developer guide...');
            setOverlayOpen(false);
        } else {
            const codebase = findCodebase(command);
            if (codebase) {
                props.changePage('documentation', codebase.codebase_id);
                addTranscript(rawCommand, `opening docs/${codebase.name}...`);
                setOverlayOpen(false);
            } else {
                addTranscript(rawCommand, `command not found: ${rawCommand}`);
            }
        }

        setQueryAndResults('');
    }

    function handleResult(result) {
        executeCommand(result.command);
    }

    function handleKeyDown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            executeCommand(query || results[0]?.command);
        } else if (event.key === 'Tab') {
            event.preventDefault();
            if (results[0]) {
                setQueryAndResults(results[0].command);
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (history.length === 0) {
                return;
            }
            const nextIndex = historyIndex === null ? history.length - 1 : Math.max(historyIndex - 1, 0);
            setHistoryIndex(nextIndex);
            setQueryAndResults(history[nextIndex]);
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (history.length === 0 || historyIndex === null) {
                return;
            }
            const nextIndex = historyIndex + 1;
            if (nextIndex >= history.length) {
                setHistoryIndex(null);
                setQueryAndResults('');
            } else {
                setHistoryIndex(nextIndex);
                setQueryAndResults(history[nextIndex]);
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setOverlayOpen(false);
            compactInputRef.current?.blur();
        }
    }

    function openOverlay() {
        setOverlayOpen(true);
        window.setTimeout(() => inputRef.current?.focus(), 0);
    }

    return (
        <>
            <div className="cli-global-prompt" onClick={openOverlay}>
                <span>&gt;</span>
                <input
                    ref={compactInputRef}
                    type="text"
                    className="cli-global-input"
                    aria-label="command line"
                    placeholder="command"
                    value={query}
                    onFocus={openOverlay}
                    onChange={(event) => {
                        setQueryAndResults(event.target.value);
                        openOverlay();
                    }}
                />
            </div>
            {overlayOpen && (
                <div className="cli-overlay" onMouseDown={() => setOverlayOpen(false)}>
                    <div className="cli-holder cli-overlay-panel" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="cli-overlay-header">
                            <h2>{CLI_NAME}</h2>
                            {/* <button className="cli-result-line cli-overlay-close" onClick={() => setOverlayOpen(false)}>esc close</button> */}
                        </div>
                        <div className="comment-block" style={{ fontSize: '0.8em', padding: '1%', width: '98%', textAlign: 'left', margin: 0, marginBottom: '10px' }}>
                            {'/* '}
                            <b>ESC</b> = close | <b>ENT</b> = execute | <b>TAB</b> = fill | <b>UP/DOWN</b> = repeat
                            {' */'}
                        </div>
                        <div className="cli-transcript">
                            {transcript.map((line, index) => (
                                <div key={index} className={line.type === 'prompt' ? 'green-code' : 'white-code'} style={{ marginLeft: 0 }}>{line.text}</div>
                            ))}
                        </div>
                        <div className="cli-div">
                            <span>&gt;&gt; </span>
                            <input
                                ref={inputRef}
                                type="text"
                                className="cli"
                                autoFocus
                                value={query}
                                onChange={(event) => setQueryAndResults(event.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="cli-results">
                            {results.map((item, index) => (
                                <button onClick={() => handleResult(item)} className="cli-result-line" key={`${item.command}-${item.display}`}>
                                    <span className="cli-result-index">{String(index + 1).padStart(2, '0')}</span>
                                    <span className="cli-result-command">{item.command}</span>
                                    {item.description && <span className="cli-result-description">{item.description}</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
